import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Toast from '../components/Toast';
import { C, E, RAYON, T, champ as champStyle } from '../theme';
import { API } from '../api';
import { IconeCorbeille } from '../components/Icones';

// Repères communs à cette page (voir src/theme.js).
const carteSection = {
  background: C.surface, borderRadius: RAYON.lg, padding: E.lg,
  border: `1px solid ${C.bordure}`,
};
const titreSection = { fontSize: '14px', fontWeight: 600, color: C.texte,
                       margin: `0 0 ${E.lg}` };


function Proposition() {
  const [prospects, setProspects] = useState([]);
  const [propositions, setPropositions] = useState([]);
  const [form, setForm] = useState({
    prospect_id: '', nom_societe: '', nom_dirigeant: '',
    adresse: '', materiel: '', montant: '', taux: '', duree: '60',
    premier_loyer_plm: '', valeur_residuelle: '', frais_dossier: '',
  });
  const [loading, setLoading] = useState(false);
  const [selectValue, setSelectValue] = useState('');
  const [reopenedFromId, setReopenedFromId] = useState(null);
  const [toast, setToast] = useState(null);
  const [loadingPropId, setLoadingPropId] = useState(null);

  const fetchPropositions = useCallback(() => {
    axios.get(`${API}/propositions`).then(res => setPropositions(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/prospects`).then(res => setProspects(res.data));
    fetchPropositions();
  }, [fetchPropositions]);

  const selectProspect = (id) => {
    const p = prospects.find(p => p.id === parseInt(id));
    if (p) setForm({ ...form, prospect_id: id, nom_societe: p.nom_societe, nom_dirigeant: p.nom_dirigeant || '', adresse: p.adresse || '' });
    setSelectValue('');
  };

  const loyer = () => {
    const montant = parseFloat(form.montant) || 0;
    const taux = parseFloat(form.taux) || 0;
    const duree = parseInt(form.duree) || 60;
    const tauxMensuel = taux / 100 / 12;
    if (tauxMensuel > 0) return (montant * tauxMensuel / (1 - Math.pow(1 + tauxMensuel, -duree))).toFixed(2);
    return (montant / duree).toFixed(2);
  };

  const genererPDF = async () => {
    setLoading(true);
    try {
      const payload = { ...form, _skip_save: !!reopenedFromId };
      const response = await axios.post(`${API}/proposition-pdf`, payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const nom = form.nom_societe ? `proposition_${form.nom_societe.replace(/\s+/g, '_')}.pdf` : 'proposition_cb_courtage.pdf';
      link.setAttribute('download', nom);
      document.body.appendChild(link);
      link.click();
      link.remove();
      if (!reopenedFromId) {
        fetchPropositions();
        setToast({ message: 'Proposition générée et sauvegardée !', type: 'success' });
      } else {
        setToast({ message: 'PDF régénéré.', type: 'success' });
      }
    } catch {
      setToast({ message: 'Erreur lors de la génération du PDF', type: 'error' });
    }
    setLoading(false);
  };

  const rouvrir = async (prop) => {
    try {
      const res = await axios.get(`${API}/propositions/${prop.id}`);
      const data = JSON.parse(res.data.donnees || '{}');
      setForm({
        prospect_id: data.prospect_id || '',
        nom_societe: data.nom_societe || '',
        nom_dirigeant: data.nom_dirigeant || '',
        adresse: data.adresse || '',
        materiel: data.materiel || '',
        montant: data.montant || '',
        taux: data.taux || '',
        duree: data.duree || '60',
        premier_loyer_plm: data.premier_loyer_plm || '',
        valeur_residuelle: data.valeur_residuelle || '',
        frais_dossier: data.frais_dossier || '',
      });
      setReopenedFromId(prop.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setToast({ message: `Proposition ${prop.numero} chargée`, type: 'success' });
    } catch {
      setToast({ message: 'Erreur lors de l\'ouverture', type: 'error' });
    }
  };

  const telechargerPDF = async (prop) => {
    setLoadingPropId(prop.id);
    try {
      const res = await axios.get(`${API}/propositions/${prop.id}`);
      const data = JSON.parse(res.data.donnees || '{}');
      data._skip_save = true;
      const response = await axios.post(`${API}/proposition-pdf`, data, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `proposition_${prop.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setToast({ message: 'PDF téléchargé !', type: 'success' });
    } catch {
      setToast({ message: 'Erreur lors de la génération du PDF', type: 'error' });
    }
    setLoadingPropId(null);
  };

  const supprimer = async (id) => {
    if (!window.confirm('Supprimer cette proposition ?')) return;
    try {
      await axios.delete(`${API}/propositions/${id}`);
      setPropositions(prev => prev.filter(p => p.id !== id));
      if (reopenedFromId === id) { setReopenedFromId(null); }
      setToast({ message: 'Proposition supprimée', type: 'success' });
    } catch {
      setToast({ message: 'Erreur lors de la suppression', type: 'error' });
    }
  };

  const inputStyle = champStyle;

  const fmtEur = (v) => v != null ? Number(v).toLocaleString('fr-FR') + ' €' : '—';

  return (
    <div style={{ fontFamily: T.police, maxWidth: '900px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={T.titre}>Proposition commerciale</h1>
        <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>Générez une proposition PDF personnalisée — chaque génération est sauvegardée automatiquement</p>
      </div>

      {/* ── Formulaire ── */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '20px', borderLeft: reopenedFromId ? '4px solid #f59e0b' : 'none' }}>
        {reopenedFromId && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px' }}>
            <span style={{ fontSize: '13px', color: '#92400e', fontWeight: '500' }}>Réouverture de {propositions.find(p => p.id === reopenedFromId)?.numero || '…'} — Le PDF sera régénéré sans créer de nouveau record
            </span>
            <button onClick={() => setReopenedFromId(null)} style={{ background: 'none', border: 'none', color: '#92400e', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
              Nouvelle proposition ✕
            </button>
          </div>
        )}

        <h2 style={titreSection}>Sélectionner une entreprise</h2>
        <select value={selectValue} onChange={e => { setSelectValue(e.target.value); selectProspect(e.target.value); setReopenedFromId(null); }} style={inputStyle}>
          <option value="">-- Choisir une entreprise --</option>
          {prospects.map(p => <option key={p.id} value={p.id}>{p.nom_societe}</option>)}
        </select>

        {form.nom_societe && (
          <div style={{ background: '#f0f7ff', borderRadius: '8px', padding: '14px', marginTop: '12px', border: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: '13px', color: '#1a2b4a', fontWeight: '600' }}>{form.nom_societe}</div>
            {form.nom_dirigeant && <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{form.nom_dirigeant}</div>}
            {form.adresse && <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{form.adresse}</div>}
          </div>
        )}
      </div>

      <div style={{ ...carteSection, marginBottom: E.lg }}>
        <h2 style={titreSection}>Détails du financement</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {[
            { key: 'materiel', label: 'Matériel' },
            { key: 'montant', label: 'Montant HT (€)' },
            { key: 'taux', label: 'Taux annuel (%)' },
            { key: 'duree', label: 'Durée (mois)' },
            { key: 'premier_loyer_plm', label: 'PLM — 1er loyer (€)' },
            { key: 'valeur_residuelle', label: 'VR — Valeur résiduelle (%)' },
            { key: 'frais_dossier', label: 'Frais de dossier (€)' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#6b7280' }}>{label}</label>
              <input value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} style={inputStyle} />
            </div>
          ))}
        </div>
      </div>

      {form.montant && form.taux && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '20px', border: '2px solid #10b981' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Loyer mensuel estimé</div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#059669' }}>{loyer()} €<span style={{ fontSize: '16px', fontWeight: '400' }}>/mois</span></div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Sur {form.duree} mois</div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Taux {form.taux}%</div>
            </div>
          </div>
        </div>
      )}

      <button onClick={genererPDF} disabled={loading || !form.nom_societe}
        style={{ width: '100%', padding: '14px', background: form.nom_societe ? '#1a2b4a' : '#e5e7eb', color: form.nom_societe ? 'white' : '#9ca3af', border: 'none', borderRadius: '8px', fontSize: '15px', cursor: form.nom_societe ? 'pointer' : 'not-allowed', fontWeight: '600', marginBottom: '40px' }}>
        {loading ? 'Génération en cours...' : reopenedFromId ? '📥 Régénérer le PDF' : '📥 Générer et sauvegarder la proposition'}
      </button>

      {/* ── Propositions sauvegardées ── */}
      <div style={{ marginTop: '8px' }}>
        <h2 style={titreSection}>Propositions sauvegardées</h2>

        {propositions.length === 0 ? (
          <div style={{ background: 'white', borderRadius: '12px', padding: '40px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>📄</div>
            <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>Aucune proposition enregistrée. Remplissez le formulaire ci-dessus pour générer votre première proposition ci-dessus.</p>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                  {['N°', 'Entreprise', 'Matériel', 'Montant HT', 'Date', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {propositions.map(prop => (
                  <tr key={prop.id} style={{ borderBottom: '1px solid #f3f4f6', background: reopenedFromId === prop.id ? '#fffbeb' : 'white' }}
                    onMouseEnter={e => { if (reopenedFromId !== prop.id) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = reopenedFromId === prop.id ? '#fffbeb' : 'white'; }}>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ fontWeight: '700', fontSize: '13px', color: '#1a2b4a', fontFamily: 'monospace' }}>{prop.numero}</span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '500', color: '#1a2b4a' }}>{prop.nom_societe || '—'}</td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#6b7280' }}>{prop.materiel || '—'}</td>
                    <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#059669' }}>{fmtEur(prop.montant)}</td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {new Date(prop.date_creation).toLocaleDateString('fr-FR')}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button onClick={() => rouvrir(prop)} style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' }}>Rouvrir
                        </button>
                        <button onClick={() => telechargerPDF(prop)} disabled={loadingPropId === prop.id}
                          style={{ background: '#1a2b4a', color: 'white', border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', fontWeight: '500', cursor: loadingPropId === prop.id ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: loadingPropId === prop.id ? 0.6 : 1 }}>
                          {loadingPropId === prop.id ? '...' : '📥 PDF'}
                        </button>
                        <button title="Supprimer" onClick={() => supprimer(prop.id)} style={{ background: 'white', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '6px', padding: '6px 8px', fontSize: '12px', cursor: 'pointer' }}><IconeCorbeille taille={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default Proposition;
